import { Box } from '@mui/material';
import { czasPetliPaska, odmianaDan } from '@/lib/tresci/pasekKomunikatow';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PODGLĄD ŻÓŁTEGO PASKA — 1:1 Z APLIKACJĄ
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Odwzorowanie `menu.page.component.html` + `.scss` z `tuszama-mobile@c9a507c4`:
 *   tło `--tsz-brand` #fabc00 · tekst #141414 · wysokość 29 px · cały pasek prowadzi do koszyka
 *   jedna „grupa" = koszyk → (gość) → KOMUNIKAT z megafonem albo „Dowóz: …" z ciężarówką
 *   dwie kopie grupy, druga `aria-hidden` — to one dają płynną pętlę marquee
 *   czas pętli = `max(11, round(8 + znaki × 0,16))` s
 *
 * ┌─ CZEGO PODGLĄD CELOWO NIE ROBI ────────────────────────────────────────────────────────────┐
 * │ Nie „poprawia" aplikacji. Odmiana „danie" i liczenie znaków są przepisane razem z jej       │
 * │ uproszczeniami — podgląd ma pokazać to, co zobaczy uczeń, a nie to, co powinien zobaczyć.  │
 * │ Koszyk jest PRZYKŁADOWY (nie ma tu ucznia), ale jego miejsce i kształt są prawdziwe —       │
 * │ komunikat zastępuje WYŁĄCZNIE „Dowóz: …", koszyk zostaje.                                   │
 * │ Treść idzie przez React jako tekst: `<b>` zobaczysz jako znaki, tak jak w aplikacji.        │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */

export interface PodgladPaskaProps {
  /** Finalny tekst komunikatu albo `null` — wtedy standardowy pasek „Dowóz: …". */
  tekst: string | null;
  koszyk?: { liczbaDan: number; kwotaGr: number };
  szkola?: string;
  gosc?: boolean;
  /** Animacja wyłączona — dla zrzutów i testów. */
  bezRuchu?: boolean;
}

const ZOLTY = '#fabc00';
const TEKST = '#141414';

function Megafon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10.5v3h3.2l6.3 4.2V6.3L6.2 10.5z" />
      <path d="M16 9a4 4 0 0 1 0 6" />
      <path d="M18.6 6.6a7.5 7.5 0 0 1 0 10.8" />
    </svg>
  );
}

function Ciezarowka() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.6 6.4h10.1v9.2H2.6z" />
      <path d="M12.7 9.5h3.7l3.1 3v3.1h-6.8z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="16.7" cy="18" r="1.6" />
    </svg>
  );
}

const kwota = (gr: number): string => (gr / 100).toFixed(2).replace('.', ',');

function Grupa({ tekst, koszyk, szkola, gosc, ukryta }: Required<Omit<PodgladPaskaProps, 'bezRuchu'>> & { ukryta: boolean }) {
  return (
    <Box component="span" aria-hidden={ukryta ? true : undefined}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '28px', pr: '28px', whiteSpace: 'nowrap' }}>
      <span data-testid={ukryta ? undefined : 'pasek-koszyk'}>
        W koszyku:{' '}
        <b>{koszyk.liczbaDan} {odmianaDan(koszyk.liczbaDan)} · {kwota(koszyk.kwotaGr)} zł</b>
      </span>
      {gosc ? <span>Oglądasz jako gość — zamówisz po zalogowaniu</span> : null}
      {tekst !== null ? (
        <Box component="span" data-testid={ukryta ? undefined : 'pasek-komunikat'}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Megafon />
          {tekst}
        </Box>
      ) : (
        <Box component="span" data-testid={ukryta ? undefined : 'pasek-dowoz'}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Ciezarowka />
          Dowóz: {szkola}
        </Box>
      )}
    </Box>
  );
}

export function PodgladPaska({
  tekst,
  koszyk = { liczbaDan: 2, kwotaGr: 2490 },
  szkola = 'Szkoła Podstawowa nr 1',
  gosc = false,
  bezRuchu = false,
}: PodgladPaskaProps) {
  const sekundy = tekst !== null ? czasPetliPaska(tekst) : 11;
  const wspolne = { tekst, koszyk, szkola, gosc };

  return (
    <Box
      role="region"
      aria-label="Podgląd żółtego paska"
      data-czas-petli={sekundy}
      sx={{
        background: ZOLTY,
        color: TEKST,
        height: 29,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        fontSize: 13,
        borderRadius: '6px',
        '@keyframes przewijaniePaska': {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-50%)' },
        },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          animation: bezRuchu ? 'none' : `przewijaniePaska ${sekundy}s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <Grupa {...wspolne} ukryta={false} />
        <Grupa {...wspolne} ukryta />
      </Box>
    </Box>
  );
}
